import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/utils/env';
import { addBreadcrumb, captureException } from './sentry';
import { STORAGE_KEYS } from '@/utils/constants';
import { v4 as uuidv4 } from 'uuid';

/**
 * A request config we've already retried once with the `monta` operator
 * fallback. Tagging it prevents an infinite retry loop if the fallback also
 * fails.
 */
type RetriableRequest = InternalAxiosRequestConfig & {
  _operatorFallbackTried?: boolean;
  _authRetried?: boolean;
};

/**
 * Pending guest sign-up. Every non-auth request waits on it so the app can
 * render (and load Google Maps) while auth is still in flight, without firing
 * token-less requests that would 401.
 */
let authGate: Promise<unknown> = Promise.resolve();

export function setAuthGate(gate: Promise<unknown>) {
  authGate = gate;
}

function isAuthRequest(url?: string): boolean {
  return url?.startsWith('/api/v1/auth/') ?? false;
}

/**
 * Get or create session UUID
 */
function getSessionUUID(): string {
  let uuid = localStorage.getItem(STORAGE_KEYS.SESSION_UUID);
  if (!uuid) {
    uuid = uuidv4();
    localStorage.setItem(STORAGE_KEYS.SESSION_UUID, uuid);
  }
  return uuid;
}

/**
 * Get browser/device identifier
 */
function getDeviceIdentifier(): string {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  // Extract browser name
  let browser = 'unknown';
  if (userAgent.includes('Chrome')) browser = 'chrome';
  else if (userAgent.includes('Firefox')) browser = 'firefox';
  else if (userAgent.includes('Safari')) browser = 'safari';
  else if (userAgent.includes('Edge')) browser = 'edge';

  return `${browser} ${platform}`;
}

/**
 * Get Meta header value (matches Flutter format)
 * Format: platform;environment;version;os-version;device
 */
function getMetaHeader(): string {
  const platform = 'web';
  const environment = env.ENV;
  const version = '1.0.0'; // Could be read from package.json
  const osVersion = 'browser'; // For web platform, this is 'browser'
  const device = getDeviceIdentifier();

  // Use simple string concatenation - no encoding needed for semicolons
  return `${platform};${environment};${version};${osVersion};${device}`;
}

/**
 * Get timezone abbreviation
 * Returns the timezone abbreviation in the format the API expects
 */
function getTimezone(): string {
  try {
    const date = new Date();

    // Get timezone short name using formatToParts
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find((part) => part.type === 'timeZoneName');

    if (timeZonePart?.value) {
      const tz = timeZonePart.value;

      // If it's a GMT offset (e.g., "GMT+1"), convert to just "GMT"
      // API might not accept offset format
      if (tz.startsWith('GMT') && tz.length > 3) {
        return 'GMT';
      }

      // Return the timezone abbreviation
      return tz;
    }

    // Fallback to GMT if we can't determine timezone
    return 'GMT';
  } catch (error) {
    console.warn('Failed to get timezone, using GMT', error);
    return 'GMT';
  }
}

/**
 * API Client instance with interceptors
 */
class ApiClient {
  private client: AxiosInstance;

  /**
   * Operator slugs the backend rejected via the `Operator` header
   * ("Invalid Application header" = operator exists but has no public web-map
   * application; "Invalid Operation header" = operator slug doesn't exist).
   * Once a slug lands here we send `monta` instead, so a bad `?operator=`
   * degrades to the default (unbranded) map rather than bricking the whole app
   * with a full-screen "Connection Error".
   */
  private rejectedOperators = new Set<string>();

  constructor() {
    this.client = axios.create({
      baseURL: env.API_URL,
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        Accept: 'application/json',
        // Operator header is set dynamically in request interceptor
      },
      timeout: 30000,
      // Note: withCredentials is NOT needed - we use Bearer tokens in headers, not cookies
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - Add required headers
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        if (!isAuthRequest(config.url)) {
          await authGate;
        }

        if (!config.headers) return config;

        // Add required Monta API headers (matching Flutter app)
        const metaHeader = getMetaHeader();
        const timezoneHeader = getTimezone();
        const uuidHeader = getSessionUUID();

        // Get current operator from URL (allows dynamic updates).
        // If the backend already rejected this slug (see the response
        // interceptor below), send `monta` instead so the map still loads.
        const urlParams = new URLSearchParams(window.location.search);
        const requestedOperator = urlParams.get('operator') || 'monta';
        const currentOperator = this.rejectedOperators.has(requestedOperator)
          ? 'monta'
          : requestedOperator;

        config.headers['Meta'] = metaHeader;
        config.headers['Timezone'] = timezoneHeader;

        // Only set Accept-Language if browser doesn't provide one, fallback to 'en'
        if (!config.headers['Accept-Language']) {
          const browserLanguage =
            navigator.language || navigator.languages?.[0] || 'en';
          config.headers['Accept-Language'] = browserLanguage;
        }

        config.headers['UUID'] = uuidHeader;
        config.headers['Operator'] = currentOperator;

        // Add access token if available
        const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add breadcrumb for Sentry
        addBreadcrumb(
          'api',
          `${config.method?.toUpperCase()} ${config.url}`,
          'info',
          {
            url: config.url,
            method: config.method,
          }
        );

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config;

        // Operator-header fallback. The public map sends `Operator: <slug>` on
        // every request; the backend rejects an unusable slug with a 400
        // ("Invalid Application header" = operator exists but has no public
        // web-map application; "Invalid Operation header" = slug doesn't
        // exist). Rather than let that brick the whole app (the first call is
        // guest auth → failure renders a full-screen "Connection Error"),
        // remember the bad slug and transparently retry the request as `monta`
        // so the map loads unbranded.
        const responseData = error.response?.data as
          | { message?: string }
          | undefined;
        const isOperatorHeaderError =
          error.response?.status === 400 &&
          typeof responseData?.message === 'string' &&
          /^Invalid (Application|Operation) header$/i.test(
            responseData.message
          );

        if (
          isOperatorHeaderError &&
          originalRequest &&
          !(originalRequest as RetriableRequest)._operatorFallbackTried
        ) {
          const rejectedOperator = String(
            originalRequest.headers?.['Operator'] ?? ''
          );
          // Only downgrade away from a non-monta slug. If `monta` itself is
          // rejected, something else is wrong — let the error surface normally.
          if (rejectedOperator && rejectedOperator !== 'monta') {
            this.rejectedOperators.add(rejectedOperator);
            (originalRequest as RetriableRequest)._operatorFallbackTried = true;
            addBreadcrumb(
              'operator',
              `Operator "${rejectedOperator}" rejected (${responseData?.message}); retrying as monta`,
              'warning',
              { operator: rejectedOperator, url: originalRequest.url }
            );
            return this.client(originalRequest);
          }
        }

        // Handle 401 Unauthorized or 403 Forbidden - both indicate invalid/expired token
        if (
          (error.response?.status === 401 || error.response?.status === 403) &&
          originalRequest
        ) {
          // Token expired or invalid - trigger re-auth
          // This will be handled by the auth store
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

          // Emit custom event for auth store to handle. Its listener starts a
          // guest sign-up synchronously, which sets the auth gate.
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));

          // Retry once with the fresh token (the request interceptor waits on
          // the gate). React Query never retries 4xx, so without this a stale
          // persisted token would leave the map empty until the next pan.
          const retriable = originalRequest as RetriableRequest;
          if (!isAuthRequest(retriable.url) && !retriable._authRetried) {
            retriable._authRetried = true;
            return this.client(retriable);
          }
        }

        // Log error to Sentry (except for expected 4xx errors)
        if (error.response) {
          const status = error.response.status;
          if (
            status >= 500 ||
            (status >= 400 &&
              status !== 401 &&
              status !== 403 &&
              status !== 404)
          ) {
            captureException(error as Error, {
              url: originalRequest?.url,
              method: originalRequest?.method,
              status,
              response: error.response.data,
            });
          }
        } else if (!axios.isCancel(error)) {
          // Network error or timeout (aborted requests are expected: React
          // Query cancels superseded viewport fetches)
          captureException(error as Error, {
            url: originalRequest?.url,
            method: originalRequest?.method,
          });
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * GET request
   */
  async get<T>(
    url: string,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ) {
    const response = await this.client.get<T>(url, { params, signal });
    return response.data;
  }

  /**
   * GET request with Location header (lat,lng format) AND query parameters
   * Sends location in both formats for maximum compatibility
   */
  async getWithLocationHeader<T>(
    url: string,
    location: { lat: number; lng: number }
  ) {
    const response = await this.client.get<T>(url, {
      params: {
        lat: location.lat,
        lng: location.lng,
      },
      headers: {
        Location: `${location.lat},${location.lng}`,
      },
    });
    return response.data;
  }

  /**
   * POST request
   */
  async post<T>(url: string, data?: unknown) {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T>(url: string, data?: unknown) {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T>(url: string) {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  /**
   * Get the raw Axios instance
   */
  getInstance() {
    return this.client;
  }
}

export const apiClient = new ApiClient();
